'use client';

import React, { useEffect, useRef } from 'react'

import { Button } from '@/components/ui/button';
import { docsContainerClass, getDocsSlug } from '@/lib/shared/utils/docs';
import { DocsNavigationAppRootResult, DocsNavigationSection, DocsNode } from '@/types/docs'
import Link from 'next/link';
import { cn } from '@/lib/shared/ui';
import { useOnClickOutside } from 'usehooks-ts';
import { ChevronsUpDown, Menu, X } from 'lucide-react';
import { slugify } from '@/lib/shared/utils/slugify';

type Props = {
  docs: DocsNavigationAppRootResult;
  slug: string;
  versions: { name: string; slug?: string }[];
  currentHeading?: string;
}

const isMatchingSlug = (fullSlug: string, currentSlug: string) => {
  const fullSlugParts = fullSlug.split('/').filter(Boolean);
  const currentSlugParts = currentSlug.split('/').filter(Boolean);

  return currentSlugParts.every((part, index) => part === fullSlugParts[index]);
}

const Waypoint = () => <div className="w-1 h-1 rounded-full bg-accent absolute -left-4 top-1/2 -translate-y-1/2" />

const FirstLevelNav = (props: { node: DocsNavigationSection, slug: string, currentHeading?: string }) => {
  const { node, slug, currentHeading } = props;

  const [expanded, setExpanded] = React.useState(isMatchingSlug(slug, node.slug));

  const handleClick = (e: React.MouseEvent) => {
    if (slug === node.slug) {
      e.preventDefault();
      setExpanded(!expanded);
    }
  }

  const hasWaypoint = slug === node.slug;

  return (
    <div className="py-3 border-b border-b-foreground/20">
      <Link href={getDocsSlug(node.slug)} onClick={handleClick} className="hover:underline cursor-pointer relative block">
        {hasWaypoint && <Waypoint />}
        <div>
          {node.name}
        </div>
      </Link>

      {expanded && node.pages?.length && (
        <div className="flex flex-col gap-2 my-3">
          {node.pages.map((child) => (
            <SecondLevelNav key={child.id} node={child} slug={slug} currentHeading={currentHeading} />
          ))}
        </div>
      )}
    </div>
  )
}

const SecondLevelNav = (props: { node: DocsNode, slug: string, currentHeading?: string }) => {
  const { node, slug, currentHeading } = props;

  const [expanded, setExpanded] = React.useState(isMatchingSlug(slug, node.slug));

  const handleClick = (e: React.MouseEvent) => {
    if (slug === node.slug) {
      e.preventDefault();
      setExpanded(!expanded);
    }
  }

  const hasWaypoint = slug === node.slug;

  return (
    <div className="pl-4 text-sm">
      <Link href={getDocsSlug(node.slug)} onClick={handleClick} className="hover:underline cursor-pointer relative block">
        {hasWaypoint && <Waypoint />}
        {node.name}
      </Link>

      {expanded && node.pages?.length && (
        <div className="flex flex-col gap-2 my-3">
          {node.pages.map((child) => (
            <ThirdLevelNav key={child.id} node={child} slug={slug} currentHeading={currentHeading} />
        ))}
        </div>
      )}
    </div>
  )
}

const ThirdLevelNav = (props: { node: DocsNode, slug: string, currentHeading?: string }) => {
  const { node } = props;

  const hash = node.slug.split('#')?.[1];
  const hasWaypoint = slugify(props.currentHeading || '') === hash;

  return (
    <div className="pl-4 text-xs relative">
      {hasWaypoint && <Waypoint />}
      <Link href={getDocsSlug(node.slug)}>{node.name}</Link>
    </div>
  )
}

export const Navigation = (props: Props) => {
  const { docs, slug, versions } = props;

  const [mobileMenuExpanded, setMobileMenuExpanded] = React.useState(false);
  const [currentHeading, setCurrentHeading] = React.useState<string>();

  const updateCurrentHeading = () => {
    const element = document.querySelector(`.${docsContainerClass}`);
    if (!element) {
      return;
    }

    const headings = element.querySelectorAll<HTMLHeadingElement>('h1,h2,h3');
    const currentHeading = Array.from(headings)
      .reverse()
      .find(
        (heading) =>
          heading.offsetTop < window.scrollY + window.innerHeight / 4.0,
      )?.id;

      setCurrentHeading(currentHeading);
  }

  useEffect(() => {
    window.addEventListener('scroll', updateCurrentHeading);

    return () => {
      window.removeEventListener('scroll', updateCurrentHeading);
    };
  }, []);

  return (
    <div className="mr-3">
      <div className="lg:hidden flex flex-col gap-2">
        <Button onClick={() => setMobileMenuExpanded(!mobileMenuExpanded)} variant="ghost" className="w-full justify-start gap-2">
          {mobileMenuExpanded ? <X /> : <Menu />}
          Menu
        </Button>
        
        {mobileMenuExpanded && (
          <NavigationMenu docs={docs} slug={slug} versions={versions} />
        )}
      </div>
      
      <div className="hidden lg:flex">
        <NavigationMenu docs={docs} slug={slug} versions={versions} currentHeading={currentHeading} />
      </div>
    </div>
  )
}

export const NavigationMenu = (props: Props) => {
  const { docs, slug, versions, currentHeading } = props;

  const elementRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);

  useOnClickOutside(elementRef as React.RefObject<HTMLDivElement>, () => setExpanded(false));

  return (
    <div className="w-full">
      <div className={cn('flex flex-col gap-3')} ref={elementRef}>
        <div className="flex justify-between items-center">
          <Link className="text-2xl font-bold hover:text-accent" href={getDocsSlug(docs.slug)}>
            {docs.name}
          </Link>

          <Button data-testid="docs-version-navigation-button" onClick={() => setExpanded(!expanded)} variant="ghost" size="icon" className="hover:text-accent">
            <ChevronsUpDown size={16} />
          </Button>
        </div>

        {expanded && (
          <div className="flex flex-col gap-2 border border-foreground/20 rounded-lg p-2">
            {versions.map((version) => {
              const isActive = version.slug && slug.includes(version.slug);
              return <div key={version.slug}>
                <Link data-testid={`docs-version-navigation-${version.slug?.replace(/\//g, '')}`} className={cn(isActive && 'hover:no-underline opacity-60 cursor-auto')} href={isActive ? '' : getDocsSlug(version.slug)}>
                  <div className={cn('py-3 px-2 rounded-lg', !isActive && 'hover:bg-foreground/10')}>
                    {version.name}
                  </div>
                </Link>
              </div>
            })}
          </div>
        )}
      </div>

      {!expanded && docs.pages?.map((child) => (
        <div key={child.id}>
          <FirstLevelNav node={child} slug={slug} currentHeading={currentHeading} />
        </div>
      ))}
    </div>
  )
}
